<div mat-dialog-title class="dialog-header">
  <mat-icon>settings</mat-icon>
  <span>Playlist Generation Settings</span>
</div>

<div mat-dialog-content class="dialog-content">
  <mat-tab-group class="settings-tabs" [(selectedIndex)]="selectedTabIndex">
    
    <!-- Basic Settings Tab -->
    <mat-tab label="Basic">
      <div class="tab-content">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Playlist Name</mat-label>
          <input matInput 
                 [(ngModel)]="settings.playlistName" 
                 placeholder="Enter playlist name"
                 (blur)="validateAndUpdate()">
          <mat-hint>Default name for generated playlists</mat-hint>
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Number of Songs</mat-label>
          <input matInput 
                 type="number" 
                 min="1" 
                 max="100"
                 [(ngModel)]="settings.playlistLength"
                 (blur)="validateAndUpdate()">
          <mat-hint>How many songs to include (1-100)</mat-hint>
        </mat-form-field>

        <div class="section">
          <h4>Preferred Genres</h4>
          <mat-chip-listbox class="genre-chips" [(ngModel)]="settings.selectedGenres">
            <mat-chip-option *ngFor="let genre of availableGenres" [value]="genre">
              {{genre}}
            </mat-chip-option>
          </mat-chip-listbox>
          <mat-hint>Select your preferred music genres (optional)</mat-hint>
        </div>
      </div>
    </mat-tab>

    <!-- Filters Tab -->
    <mat-tab label="Filters">
      <div class="tab-content">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>History Filter (Days)</mat-label>
          <input matInput 
                 type="number" 
                 min="0" 
                 max="365"
                 [(ngModel)]="settings.historyFilterDays"
                 (blur)="validateAndUpdate()">
          <mat-hint>Exclude songs played within this many days (0 = no filter)</mat-hint>
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Maximum Play Count</mat-label>
          <input matInput 
                 type="number" 
                 min="1" 
                 max="10"
                 [(ngModel)]="settings.maximumPlayCount"
                 (blur)="validateAndUpdate()">
          <mat-hint>Skip songs played more than this many times</mat-hint>
        </mat-form-field>

        <div class="checkbox-section">
          <mat-checkbox [(ngModel)]="settings.excludeRecentTracks" 
                        (change)="validateAndUpdate()">
            Exclude recently played tracks
          </mat-checkbox>
          <mat-hint>Automatically filter out tracks from your recent listening history</mat-hint>
        </div>

        <div class="checkbox-section">
          <mat-checkbox [(ngModel)]="settings.excludeDislikedArtists" 
                        (change)="validateAndUpdate()">
            Exclude disliked artists
          </mat-checkbox>
          <mat-hint>Skip tracks from artists you've marked as disliked</mat-hint>
        </div>
      </div>
    </mat-tab>

    <!-- Advanced Tab -->
    <mat-tab label="Advanced">
      <div class="tab-content">
        <div class="section">
          <h4>Popularity Range</h4>
          <div class="range-inputs">
            <mat-form-field appearance="outline">
              <mat-label>Min</mat-label>
              <input matInput 
                     type="number" 
                     min="0" 
                     max="100"
                     [ngModel]="getPopularityMin()"
                     (ngModelChange)="setPopularityMin($event)"
                     (blur)="validateAndUpdate()">
            </mat-form-field>
            <span class="range-separator">to</span>
            <mat-form-field appearance="outline">
              <mat-label>Max</mat-label>
              <input matInput 
                     type="number" 
                     min="0" 
                     max="100"
                     [ngModel]="getPopularityMax()"
                     (ngModelChange)="setPopularityMax($event)"
                     (blur)="validateAndUpdate()">
            </mat-form-field>
          </div>
          <mat-hint>Filter tracks by popularity score (0-100)</mat-hint>
          
          <mat-checkbox [(ngModel)]="enablePopularityFilter" 
                        (change)="togglePopularityFilter()">
            Enable popularity filtering
          </mat-checkbox>
        </div>

        <div class="section">
          <h4>Year Range</h4>
          <div class="range-inputs">
            <mat-form-field appearance="outline">
              <mat-label>From</mat-label>
              <input matInput 
                     type="number" 
                     min="1900" 
                     [max]="currentYear"
                     [(ngModel)]="tempYearRange.min"
                     (blur)="validateAndUpdate()">
            </mat-form-field>
            <span class="range-separator">to</span>
            <mat-form-field appearance="outline">
              <mat-label>To</mat-label>
              <input matInput 
                     type="number" 
                     min="1900" 
                     [max]="currentYear"
                     [(ngModel)]="tempYearRange.max"
                     (blur)="validateAndUpdate()">
            </mat-form-field>
          </div>
          <mat-hint>Filter tracks by release year</mat-hint>
          
          <mat-checkbox [(ngModel)]="enableYearFilter" 
                        (change)="toggleYearFilter()">
            Enable year filtering
          </mat-checkbox>
        </div>

        <div class="preview-section" *ngIf="trackCountEstimate !== null">
          <mat-icon>info</mat-icon>
          <span>Estimated {{trackCountEstimate}} matching tracks</span>
        </div>
      </div>
    </mat-tab>

    <!-- API Keys Tab -->
    <mat-tab label="AI Provider">
      <div class="tab-content">
        <div class="api-key-section">
          <h4>
            <mat-icon>smart_toy</mat-icon>
            AI Provider
          </h4>
          <p class="description">
            Which AI builds your playlists. If it fails (out of credit, usage limit, sign-in expired),
            the other set-up providers are tried automatically.
          </p>
          <mat-button-toggle-group class="provider-toggle"
                                   [value]="selectedProvider"
                                   (change)="selectProvider($event.value)"
                                   aria-label="AI provider">
            <mat-button-toggle value="codex">ChatGPT</mat-button-toggle>
            <mat-button-toggle value="openai">OpenAI API</mat-button-toggle>
            <mat-button-toggle value="gemini">Gemini</mat-button-toggle>
          </mat-button-toggle-group>
        </div>

        <!-- ChatGPT subscription via the Codex CLI -->
        <div class="api-key-section" *ngIf="selectedProvider === 'codex'">
          <h4>
            <mat-icon>chat</mat-icon>
            ChatGPT subscription
          </h4>
          <p class="description">
            Uses your ChatGPT plan (through OpenAI's Codex) instead of a paid API key.
            Playlists count against your plan's Codex usage and take 30–60 seconds to generate.
          </p>

          <ng-container *ngIf="providerStatus as status">
            <div class="current-status" *ngIf="!status.codex.allowed">
              <mat-icon class="no-key">block</mat-icon>
              <span>ChatGPT isn't enabled for this account on this server.</span>
            </div>

            <ng-container *ngIf="status.codex.allowed">
              <!-- Signed in -->
              <ng-container *ngIf="status.codex.signedIn; else notSignedIn">
                <div class="current-status">
                  <mat-icon class="has-key">verified</mat-icon>
                  <span>Signed in to ChatGPT</span>
                </div>
                <div class="api-key-actions">
                  <button mat-stroked-button [disabled]="codexBusy" (click)="signOutOfChatGPT()">
                    <mat-icon>logout</mat-icon>
                    Sign out
                  </button>
                </div>
              </ng-container>

              <!-- Not signed in -->
              <ng-template #notSignedIn>
                <ng-container *ngIf="codexLogin as login; else signInButton">
                  <ol class="codex-steps">
                    <li>
                      Open
                      <a [href]="login.url" target="_blank" rel="noopener">{{ login.url }}</a>
                      and sign in to your ChatGPT account.
                    </li>
                    <li>
                      Enter this code:
                      <div class="codex-code">
                        <span>{{ login.code }}</span>
                        <button mat-icon-button (click)="copyCodexCode()" matTooltip="Copy code" aria-label="Copy code">
                          <mat-icon>content_copy</mat-icon>
                        </button>
                      </div>
                      <span class="hint">The code expires {{ login.expiresAt | date:'shortTime' }}.</span>
                    </li>
                    <li class="waiting">
                      <mat-spinner diameter="18"></mat-spinner>
                      Waiting for you to finish signing in…
                    </li>
                  </ol>
                </ng-container>
                <ng-template #signInButton>
                  <div class="current-status">
                    <mat-icon class="no-key">key_off</mat-icon>
                    <span>Not signed in</span>
                  </div>
                  <div class="api-key-actions">
                    <button mat-raised-button color="primary" [disabled]="codexBusy" (click)="signInToChatGPT()">
                      <mat-icon>login</mat-icon>
                      {{ codexBusy ? 'Starting…' : 'Sign in with ChatGPT' }}
                    </button>
                  </div>
                </ng-template>
              </ng-template>
            </ng-container>
          </ng-container>

          <div class="api-key-status" *ngIf="codexMessage as message">
            <mat-icon [class]="message.ok ? 'success' : 'error'">{{ message.ok ? 'check_circle' : 'error' }}</mat-icon>
            <span>{{ message.text }}</span>
          </div>
        </div>

        <!-- API key for the selected provider (Gemini or OpenAI) -->
        <div class="api-key-section" *ngIf="selectedKeyProvider as provider">
          <h4>
            <mat-icon>key</mat-icon>
            {{provider.label}} API Key
          </h4>
          <p class="description">
            Use your own {{provider.label}} key (billed per use by {{provider.label}}).
            It is encrypted and stored securely, and saved immediately.
          </p>

          <div class="current-status" *ngIf="keyInfo[provider.service] as info">
            <mat-icon [class]="info.hasKey ? 'has-key' : 'no-key'">
              {{info.hasKey ? 'verified' : 'key_off'}}
            </mat-icon>
            <span *ngIf="info.hasKey; else noKeyTemplate">
              Using personal key ({{info.hint}})
            </span>
            <ng-template #noKeyTemplate>
              <span>Using the server's key (if one is configured)</span>
            </ng-template>
          </div>

          <mat-form-field appearance="outline" class="full-width api-key-input">
            <mat-label>Enter your {{provider.label}} API key</mat-label>
            <input matInput
                   type="password"
                   autocomplete="off"
                   [(ngModel)]="newApiKey[provider.service]"
                   [placeholder]="provider.placeholder">
            <mat-hint>
              Get a key from
              <a [href]="provider.keyUrl" target="_blank" rel="noopener">{{provider.keySite}}</a>
            </mat-hint>
          </mat-form-field>

          <div class="api-key-actions">
            <button mat-raised-button
                    color="primary"
                    [disabled]="!newApiKey[provider.service] || testingKey[provider.service]"
                    (click)="testAndSaveApiKey(provider.service)">
              <mat-icon>{{testingKey[provider.service] ? 'hourglass_empty' : 'save'}}</mat-icon>
              {{testingKey[provider.service] ? 'Testing...' : 'Test & Save'}}
            </button>

            <button mat-stroked-button
                    *ngIf="keyInfo[provider.service]?.hasKey"
                    (click)="removeApiKey(provider.service)">
              <mat-icon>delete</mat-icon>
              Remove Key
            </button>
          </div>

          <div class="api-key-status" *ngIf="keyTestResult[provider.service] as result">
            <mat-icon [class]="result.valid ? 'success' : 'error'">
              {{result.valid ? 'check_circle' : 'error'}}
            </mat-icon>
            <span>
              {{result.message || (result.valid ? 'API key is valid!' : 'Invalid API key. Please check and try again.')}}
            </span>
          </div>
        </div>
      </div>
    </mat-tab>

  </mat-tab-group>
</div>

<div mat-dialog-actions class="dialog-actions">
  <button mat-button (click)="onCancel()">Cancel</button>
  <button mat-button (click)="resetToDefaults()" color="warn">Reset to Defaults</button>
  <button mat-raised-button (click)="onSave()" color="primary">Save Settings</button>
</div>

<!-- Validation Errors -->
<div class="validation-errors" *ngIf="validationErrors.length > 0">
  <mat-error *ngFor="let error of validationErrors">
    <mat-icon>error</mat-icon>
    {{error}}
  </mat-error>
</div>